import {
  AlertTriangle,
  Calendar,
  Check,
  ClipboardList,
  FilePenLine,
  Info,
  ListChecks,
  Lock,
  Save,
  ShieldCheck,
  GitBranch,
  ArrowLeft,
} from "lucide-react";
import { useTran } from "@/hooks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import { getFrameworkMeta } from "../../constants/framework-metadata";
import { CalendarCheck2Icon } from "lucide-react";
import { Building2 } from "lucide-react";
import { resolveLogoUrl } from "@/lib/utils";

const getNodeId = (node) => node?.id || node?._id || node?.key || node?.name;

const getFrameworkDisplay = (framework) => {
  const meta = getFrameworkMeta(framework.code);

  return {
    code: framework.code,
    shortName: framework.name || meta.shortName || framework.code,
    fullName:
      framework.fullName || framework.name || meta.label || framework.code,
    description: meta.description || meta.summary || "",
    // Custom frameworks have no entry in the static standard-framework
    // metadata (so meta.image is always undefined for them) — use the
    // uploaded logo the /frameworks endpoint resolves instead. Local
    // storage returns a relative /static/... path that needs the API's
    // origin prepended (UI/API run on different dev ports).
    image: resolveLogoUrl(framework.logoUrl) || meta.image,
  };
};

const SectionHeader = ({ number, title }) => (
  <div className="flex items-center gap-3 border-b border-border px-4 py-3 sticky">
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-radius bg-primary/10 text-primary">
      {number === "1" ? (
        <CalendarCheck2Icon className="h-5 w-5 text-primary" />
      ) : number === "2" ? (
        <ListChecks className="h-5 w-5 text-primary" />
      ) : number === "3" ? (
        <Building2 className="h-5 w-5 text-primary" />
      ) : null}
    </span>
    <h3 className="text-sm font-bold text-foreground">{title}</h3>
  </div>
);

const DetailRow = ({ label, value, badge }) => (
  <div className="flex items-center justify-between gap-4 border-b border-border/80 py-3 last:border-b-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-right text-sm font-semibold text-foreground">
      {badge ?? value}
    </span>
  </div>
);

const FrameworkItem = ({ selectedFramework, frameworks }) => {
  const framework = frameworks.find((item) => item.code === selectedFramework);

  if (!framework) return null;

  const display = getFrameworkDisplay(framework);

  return (
    <div className="flex gap-3 rounded-radius border border-border  p-3 shadow-sm">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full p-1.5 text-xs font-bold text-primary">
        {display.image ? (
          <img
            src={display.image}
            alt={`${display.fullName} logo`}
            className="max-h-full max-w-full rounded-full object-contain"
          />
        ) : (
          display.fullName?.charAt(0)
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="text-sm font-bold text-foreground">
            {display.fullName}
          </p>

          {display.recommended && (
            <Badge className="border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-700">
              {t("Recommended")}
            </Badge>
          )}
        </div>

        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          {display.description}
        </p>
      </div>
    </div>
  );
};

const flattenTreeWithBreadcrumbs = (nodes = [], parentChain = []) => {
  const result = [];

  nodes.forEach((node) => {
    const id = getNodeId(node);
    const current = {
      id,
      label: node.name,
      status: node?.status,
      inactiveDate: node?.inactiveDate,
    };
    const chain = [...parentChain, current];

    result.push({ id, chain });

    if (node.children?.length) {
      result.push(...flattenTreeWithBreadcrumbs(node.children, chain));
    }
  });

  return result;
};

const OrganisationBreadcrumb = ({ chain }) => (
  <div className="rounded-radius border border-border  px-3 py-2">
    <div className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-1 text-xs leading-5">
      <GitBranch className="h-3 w-3 text-blue-600" />
      {chain.map((item, index) => {
        const isLast = index === chain.length - 1;

        return (
          <span
            key={`${item.id}-${index}`}
            className={
              isLast ? "font-semibold text-blue-600" : "text-muted-foreground"
            }
          >
            {index > 0 ? (
              <span className="px-1 font-normal text-muted-foreground/50">
                {">"}
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1">
              {item.label}
              <OrgStatusTag
                status={item.status}
                inactiveDate={item.inactiveDate}
                className="text-[10px]"
              />
            </span>
          </span>
        );
      })}
    </div>
  </div>
);

const IMPORTANT_NOTES = [
  {
    icon: ListChecks,
    text: "After saving, you can assign metrics and set targets for the selected frameworks.",
  },
  {
    icon: ShieldCheck,
    text: "Only organisations with active ESG data access are included.",
  },
  {
    icon: Lock,
    text: "Once metrics are assigned, targets configured, or data entry has started, the reporting year cannot be modified in ways that impact existing worth of data.",
  },
  {
    icon: FilePenLine,
    text: "You can add new frameworks or organisations anytime in Reporting Boundary settings.",
  },
];

const StepReviewConfirm = ({
  formData,
  onBack,
  onCancel,
  onSubmit,
  organizationTree,
  frameworks,
  isEditForm = false,
  isChanged = false,
}) => {
  const { t } = useTran();

  const organizationCount = formData.organizations.length;
  const ALL_ORG_BREADCRUMBS = flattenTreeWithBreadcrumbs(organizationTree);

  const getInclusiveMonthCount = (startYear, startMonth, endYear, endMonth) => {
    if (!startYear || !startMonth || !endYear || !endMonth) return 0;
    return (endYear - startYear) * 12 + endMonth - startMonth + 1;
  };

  const formatYearMonth = (year, month) => {
    if (!year || !month) return "-";

    return new Date(year, month - 1).toLocaleString("en", {
      month: "long",
      year: "numeric",
    });
  };

  const durationMonths = getInclusiveMonthCount(
    formData.startYear,
    formData.startMonth,
    formData.endYear,
    formData.endMonth,
  );

  return (
    <div className="space-y-4">
      <div className="rounded-radius border border-border bg-card p-4 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-radius bg-primary/10 text-primary">
              <ClipboardList className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-bold text-primary">
                {t("Review Your Selections")}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t(
                  "Please review all details before saving the reporting year configuration.",
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[0.9fr_1.25fr_1.2fr]">
        <section className="max-h-[460px] overflow-y-auto rounded-radius border border-border bg-card shadow-sm">
          <SectionHeader number="1" title={t("Reporting Period Details")} />

          <div className="p-4">
            <div className="rounded-radius border border-border  px-4">
              <DetailRow
                label={t("Start Year & Month")}
                value={formatYearMonth(formData.startYear, formData.startMonth)}
              />

              <DetailRow
                label={t("End Year & Month")}
                value={formatYearMonth(formData.endYear, formData.endMonth)}
              />
              <DetailRow
                label={t("Duration")}
                value={`${durationMonths} ${t("months")}`}
              />
            </div>

            <div className="mt-4 flex gap-3 rounded-radius border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 ">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />

              <p className="text-xs  text-muted-foreground dark:text-background">
                {t(
                  "A 12-month reporting period will be created based on the selected start month. The end month is calculated automatically. You can update the reporting period later before any reporting activities begin.",
                )}
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-radius border border-border bg-card shadow-sm overflow-hidden">
          <SectionHeader
            number="2"
            title={`${t("Selected Frameworks")} (${formData.frameworks.length})`}
          />

          <div className="max-h-[460px] overflow-y-auto">
            <div className="space-y-3 p-4">
              {formData.frameworks.map((code) => (
                <FrameworkItem
                  key={code}
                  selectedFramework={code}
                  frameworks={frameworks}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-radius border border-border bg-card shadow-sm overflow-hidden">
          <SectionHeader
            number="3"
            title={`${t("Selected Organizations")} (${organizationCount})`}
          />

          <div className="max-h-[460px] overflow-y-auto">
            <div className="p-4">
              <div>
                <p className="mb-2 text-sm font-bold text-foreground">
                  {t("Organisation Hierarchy View")}
                </p>

                <div className="space-y-2">
                  {ALL_ORG_BREADCRUMBS.filter((item) =>
                    (formData.organizations || []).includes(item.id),
                  ).map((item) => (
                    <OrganisationBreadcrumb
                      key={item.chain.map((part) => part.label).join(">")}
                      chain={item.chain}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_0.98fr]">
        {/* <div className="rounded-radius border border-amber-200 bg-amber-50/70 p-4">
          <div className="flex gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div>
              <p className="font-bold text-amber-800">{t("Important Notes")}</p>
              <div className="mt-3 space-y-2 text-xs text-foreground dark:text-background">
                {IMPORTANT_NOTES.map((note) => {
                  const Icon = note.icon;

                  return (
                    <div key={note.text} className="flex gap-2">
                      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                      <span>{t(note.text)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div> */}
      </div>

      <div className="rounded-radius border border-emerald-200 bg-emerald-50/70 p-4">
        <div className="flex gap-3">
          <Calendar className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div>
            <p className="font-bold text-emerald-800">
              {t("What happens next?")}
            </p>
            <div className="mt-3 space-y-2 text-xs text-foreground dark:text-background">
              {[
                "You can start assigning metrics to the selected frameworks.",
                "You can begin target setup and data entry.",
              ].map((item) => (
                <div key={item} className="flex gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <span>{t(item)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-radius border border-border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <Button type="button" variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
          {t("Back")}
        </Button>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t("Cancel")}
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={!isChanged && isEditForm}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            {isEditForm
              ? t("Update Reporting Year")
              : t("Create Reporting Year")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StepReviewConfirm;
